import * as type from '../../../actions/index.types';

// One reducer for the seven VMS modules that hang off a visitor record. They
// share an identical shape — a paginated list, a detail record, a save flag and
// a flag for the module's side actions — so each lives as a named slice rather
// than in seven near-identical files. Components read
// `state.VmsReducer.visits.list`, `…preApprovals.detailsLoading`, and so on.
//
// `saving` covers create and update; `acting` covers everything else that
// mutates a record (approve, revoke, complete, delete…) so a screen can disable
// a whole row of buttons from one flag without them fighting each other.

const emptySlice = () => ({
    list: [],
    pagination: null,
    listLoading: false,
    details: null,
    detailsLoading: false,
    saving: false,
    acting: false,
    error: null,
});

const initialState = {
    visits: emptySlice(),
    preApprovals: emptySlice(),
    gatePasses: emptySlice(),
    deliveries: emptySlice(),
    cabs: emptySlice(),
    activityLogs: emptySlice(),

    // Settings is a single row per society, not a list — its own shape.
    settings: {
        data: null,
        loading: false,
        saving: false,
        error: null,
    },

    // Export returns JSON rather than a file, so the rows land here and the
    // screen builds the CSV from them.
    exportLoading: false,
    exportRows: null,
};

// Which slice each action touches, and how it should be treated.
//   list    → paginated collection
//   details → single record
//   save    → create / update
//   act     → any other mutation (approve, revoke, complete, delete…)
const ROUTES = {
    // visits
    [type.SOCIETY_GET_VISITS_LIST]: ['visits', 'list'],
    [type.SOCIETY_GET_VISIT_DETAILS]: ['visits', 'details'],
    [type.SOCIETY_CREATE_VISIT]: ['visits', 'save'],
    [type.SOCIETY_UPDATE_VISIT]: ['visits', 'save'],
    [type.SOCIETY_DELETE_VISIT]: ['visits', 'act'],
    [type.SOCIETY_APPROVE_VISIT]: ['visits', 'act'],
    [type.SOCIETY_REJECT_VISIT]: ['visits', 'act'],
    [type.SOCIETY_VERIFY_VISIT_OTP]: ['visits', 'act'],
    [type.SOCIETY_CHECK_IN_VISIT]: ['visits', 'act'],
    [type.SOCIETY_CHECK_OUT_VISIT]: ['visits', 'act'],

    // pre-approvals
    [type.SOCIETY_GET_PRE_APPROVALS_LIST]: ['preApprovals', 'list'],
    [type.SOCIETY_GET_PRE_APPROVAL_DETAILS]: ['preApprovals', 'details'],
    [type.SOCIETY_CREATE_PRE_APPROVAL]: ['preApprovals', 'save'],
    [type.SOCIETY_UPDATE_PRE_APPROVAL]: ['preApprovals', 'save'],
    [type.SOCIETY_DELETE_PRE_APPROVAL]: ['preApprovals', 'act'],

    // gate passes
    [type.SOCIETY_GET_GATE_PASSES_LIST]: ['gatePasses', 'list'],
    [type.SOCIETY_GET_GATE_PASS_DETAILS]: ['gatePasses', 'details'],
    [type.SOCIETY_CREATE_GATE_PASS]: ['gatePasses', 'save'],
    [type.SOCIETY_UPDATE_GATE_PASS]: ['gatePasses', 'save'],
    [type.SOCIETY_DELETE_GATE_PASS]: ['gatePasses', 'act'],
    [type.SOCIETY_REVOKE_GATE_PASS]: ['gatePasses', 'act'],

    // deliveries
    [type.SOCIETY_GET_DELIVERIES_LIST]: ['deliveries', 'list'],
    [type.SOCIETY_GET_DELIVERY_DETAILS]: ['deliveries', 'details'],
    [type.SOCIETY_CREATE_DELIVERY]: ['deliveries', 'save'],
    [type.SOCIETY_UPDATE_DELIVERY]: ['deliveries', 'save'],
    [type.SOCIETY_DELETE_DELIVERY]: ['deliveries', 'act'],
    [type.SOCIETY_COMPLETE_DELIVERY]: ['deliveries', 'act'],

    // cabs
    [type.SOCIETY_GET_CABS_LIST]: ['cabs', 'list'],
    [type.SOCIETY_GET_CAB_DETAILS]: ['cabs', 'details'],
    [type.SOCIETY_CREATE_CAB]: ['cabs', 'save'],
    [type.SOCIETY_UPDATE_CAB]: ['cabs', 'save'],
    [type.SOCIETY_DELETE_CAB]: ['cabs', 'act'],
    [type.SOCIETY_COMPLETE_CAB]: ['cabs', 'act'],

    // activity logs (read-only)
    [type.SOCIETY_GET_VMS_ACTIVITY_LOGS]: ['activityLogs', 'list'],
};

const LOADING_KEY = { list: 'listLoading', details: 'detailsLoading', save: 'saving', act: 'acting' };

const withSlice = (state, name, patch) => ({
    ...state,
    [name]: { ...state[name], ...patch },
});

export function VmsReducer(state = initialState, action) {
    const base = String(action.type)
        .replace(/_SUCCESS$/, '')
        .replace(/_FAILURE$/, '');
    const route = ROUTES[base];

    if (route) {
        const [name, kind] = route;
        const flag = LOADING_KEY[kind];

        if (action.type === base) {
            return withSlice(state, name, { [flag]: true, error: null });
        }

        if (action.type === `${base}_SUCCESS`) {
            if (kind === 'list') {
                return withSlice(state, name, {
                    listLoading: false,
                    list: action.payload?.rows || [],
                    pagination: action.payload?.pagination || null,
                    error: null,
                });
            }
            if (kind === 'details') {
                return withSlice(state, name, { detailsLoading: false, details: action.payload, error: null });
            }
            return withSlice(state, name, { [flag]: false, error: null });
        }

        if (action.type === `${base}_FAILURE`) {
            const patch = { [flag]: false, error: action?.message };
            // A failed detail fetch means the record is gone (404 covers missing
            // and soft-deleted alike), so clear it rather than leaving the
            // previous visitor's record on screen.
            if (kind === 'details') patch.details = null;
            return withSlice(state, name, patch);
        }
    }

    switch (action.type) {
        case type.SOCIETY_GET_VMS_SETTINGS:
            return { ...state, settings: { ...state.settings, loading: true, error: null } };
        case type.SOCIETY_GET_VMS_SETTINGS_SUCCESS:
            return { ...state, settings: { ...state.settings, loading: false, data: action.payload, error: null } };
        case type.SOCIETY_GET_VMS_SETTINGS_FAILURE:
            return { ...state, settings: { ...state.settings, loading: false, error: action?.message } };

        case type.SOCIETY_UPDATE_VMS_SETTINGS:
            return { ...state, settings: { ...state.settings, saving: true, error: null } };
        case type.SOCIETY_UPDATE_VMS_SETTINGS_SUCCESS:
            // The PUT returns the patched row, so adopt it instead of re-fetching.
            return { ...state, settings: { ...state.settings, saving: false, data: action.payload, error: null } };
        case type.SOCIETY_UPDATE_VMS_SETTINGS_FAILURE:
            return { ...state, settings: { ...state.settings, saving: false, error: action?.message } };

        case type.SOCIETY_EXPORT_VMS_ACTIVITY_LOGS:
            return { ...state, exportLoading: true, exportRows: null };
        case type.SOCIETY_EXPORT_VMS_ACTIVITY_LOGS_SUCCESS:
            return { ...state, exportLoading: false, exportRows: action.payload || [] };
        case type.SOCIETY_EXPORT_VMS_ACTIVITY_LOGS_FAILURE:
            return { ...state, exportLoading: false, exportRows: null };

        default:
            return state;
    }
}

export default VmsReducer;
